import axios from "axios";
import { getApiUrl } from "../config";

// Create an axios instance with default configs (baseURL set dynamically)
const api = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 seconds for regular requests (increased for cPanel)
});

// Create a separate instance for file uploads with optimized settings
const apiUpload = axios.create({
  timeout: 0, // No timeout - let uploads take as long as needed
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// Fast upload function (no retries for normal cases - only retry on actual network failures)
const fastUpload = async (url: string, formData: FormData): Promise<any> => {
  console.log("🚀 Starting fast upload...");

  return apiUpload.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round(
        (progressEvent.loaded * 100) / (progressEvent.total || 1),
      );

      // High-frequency progress updates for smooth UI
      window.dispatchEvent(
        new CustomEvent("uploadProgress", {
          detail: {
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: percentCompleted,
            speed: progressEvent.rate || 0,
          },
        }),
      );
    },
  });
};

// Retry upload only for network errors (not server errors)
const uploadWithRetry = async (
  url: string,
  formData: FormData,
  retries = 2,
): Promise<any> => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Upload attempt ${attempt} of ${retries}`);
      return await fastUpload(url, formData);
    } catch (error: any) {
      console.error(`Upload attempt ${attempt} failed:`, error);

      // Only retry on network errors, not server errors
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.code === "ENOTFOUND";

      if (!isNetworkError || attempt === retries) {
        throw error; // Don't retry server errors or on last attempt
      }

      // Short delay before retry for network errors only
      const delay = 1000 * attempt; // 1s, 2s
      console.log(
        `Network error detected. Retrying in ${delay / 1000} seconds...`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error("All upload attempts failed");
};

// Debug API calls (only in development)
const isDevelopment = process.env.NODE_ENV === "development";

api.interceptors.request.use(
  (config) => {
    // Always set baseURL dynamically to ensure it's current
    const apiUrl = getApiUrl();
    config.baseURL = apiUrl;

    // Ensure proper headers for CORS
    config.headers["Accept"] = "application/json";

    // Only set Content-Type to application/json if not already set (e.g. for multipart/form-data)
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    if (isDevelopment) {
      console.error("Request error:", error);
    }
    return Promise.reject(error);
  },
);

// Add interceptor for upload instance too
apiUpload.interceptors.request.use(
  (config) => {
    // Always set baseURL dynamically to ensure it's current
    config.baseURL = getApiUrl();

    if (isDevelopment) {
      console.log(
        `API Upload Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`,
      );
    }

    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    if (isDevelopment) {
      console.error("Upload request error:", error);
    }
    return Promise.reject(error);
  },
);

// Add a response interceptor for handling common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle authentication errors
      if (error.response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        // Don't redirect automatically if already on login page
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }

      if (isDevelopment) {
        console.error(
          `API Error (${error.response.status}):`,
          error.response.data,
        );
      }
    } else if (error.request) {
      // Network error - this is common in cPanel environments or CORS issues
      // The request was made but no response was received
      const apiUrl = getApiUrl();
      const requestUrl = error.config
        ? `${error.config.baseURL}${error.config.url}`
        : "unknown";

      // Log detailed error information for debugging
      console.error("API Network Error Details:", {
        message: error.message,
        code: error.code,
        requestUrl,
        apiUrl,
        origin: window.location.origin,
        timestamp: new Date().toISOString(),
      });

      if (isDevelopment) {
        console.error("API Error: No response received", error.request);
        console.log("Request details:", error.config);
        console.log(
          "This might be a CORS issue. Check backend ALLOWED_ORIGINS includes:",
          window.location.origin,
        );
      }

      // Provide a more helpful error message
      if (
        error.code === "ERR_NETWORK" ||
        error.message.includes("Network Error")
      ) {
        error.message = `Network error: Unable to connect to ${apiUrl}. This might be a CORS issue. Please check that the backend allows requests from ${window.location.origin}`;
      } else {
        error.message =
          "Network error: Unable to connect to server. Please check your internet connection.";
      }
    } else {
      if (isDevelopment) {
        console.error("API Error:", error.message);
      }
    }

    return Promise.reject(error);
  },
);

// Add response interceptor for upload instance
apiUpload.interceptors.response.use(
  (response) => {
    if (isDevelopment) {
      console.log(`API Upload Response (${response.status}):`, response.data);
    }
    return response;
  },
  (error) => {
    if (isDevelopment) {
      if (error.response) {
        console.error(
          `API Upload Error (${error.response.status}):`,
          error.response.data,
        );
      } else if (error.request) {
        console.error("API Upload Error: No response received", error.request);
        console.log("Upload request details:", error.config);
      } else {
        console.error("API Upload Error:", error.message);
      }
    }

    return Promise.reject(error);
  },
);

// Get the base server URL (without /api)
export const getServerUrl = (): string => {
  // Get server URL from config
  return getApiUrl().replace("/api", "");
};

// Helper function to get full image URL
export const getImageUrl = (relativePath: string): string => {
  if (!relativePath) {
    return "";
  }

  // If it's already a full URL, return as is
  if (
    relativePath.startsWith("http://") ||
    relativePath.startsWith("https://")
  ) {
    return relativePath;
  }

  // Clean up the path - remove any "undefined" prefix and other malformed parts
  let cleanPath = relativePath;

  // Remove "undefined" prefix if present
  if (cleanPath.startsWith("undefined/")) {
    cleanPath = cleanPath.replace("undefined/", "");
  }

  // Remove "null" prefix if present
  if (cleanPath.startsWith("null/")) {
    cleanPath = cleanPath.replace("null/", "");
  }

  // Remove any leading/trailing whitespace
  cleanPath = cleanPath.trim();

  // If the path is empty after cleaning, return empty string
  if (!cleanPath) {
    return "";
  }

  // Ensure the path starts with /
  const path = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;

  // Get the server URL - use fallback if not available
  let serverUrl = getServerUrl();

  // If serverUrl is empty or undefined, use the default production URL
  if (!serverUrl || serverUrl === "undefined" || serverUrl === "null") {
    // Try to get from env or config
    serverUrl =
      process.env.REACT_APP_SERVER_URL || "https://student.aitthisone.com";
    console.log("Using fallback server URL:", serverUrl);
  }

  return `${serverUrl}${path}`;
};

export default api;
export { apiUpload, fastUpload, uploadWithRetry };
